import { useState, useEffect, useCallback } from 'react';
import { useTranslationWithLanguage } from '../context/language-context';
import { useAuth } from 'react-oidc-context';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Calendar,
  Clock,
  X,
  AlertTriangle,
  Check,
  Search,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { format, addDays, startOfWeek, addMinutes, isSameDay, parseISO } from 'date-fns';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

// Interfaces
interface Booking {
  id: string;
  task_id: string;
  establishment_id: string;
  start_time: string | Date;
  end_time: string | Date;
  // Optional original UTC times for API calls
  original_start_time?: string;
  original_end_time?: string;
  visit_type: string;
  location: string;
  person_id: string;
  // Additional fields for UI display
  establishment?: {
    name_en?: string;
    name_ar?: string;
  };
  person?: {
    full_name_en?: string;
    full_name_ar?: string;
  };
}

interface Task {
  id: string;
  establishment_id: string;
  establishment: {
    id: string;
    name_en: string;
    name_ar: string;
  };
}

interface EstablishmentOwner {
  person_id: string;
  establishment_id: string;
  primary_email: string;
  primary_mobile: string;
  primary_faxno: string;
  primary_landline: string;
  id: string;
  full_name_en: string;
  full_name_ar: string;
}

interface TimeSlot {
  start: Date;
  end: Date;
  isBooked: boolean;
}

// Base URL from environment variables
const BASE_URL = import.meta.env.VITE_BASE_URL;

export default function Schedule() {
  const { t, language } = useTranslationWithLanguage();
  const auth = useAuth();
  const navigate = useNavigate();
  // State
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showSummaryView, setShowSummaryView] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    visit_type: 'on-site',
    location: '',
    establishment_id: '',
    person_id: '',
    task_id: '',
    start_time: '',
    end_time: '',
    // Add date objects for display
    start_date: new Date(),
    end_date: new Date()
  });
  const [owners, setOwners] = useState<EstablishmentOwner[]>([]);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [slotDuration, setSlotDuration] = useState(30); // Duration in minutes (30 min to 8 hours)

  // Generate time slots (7 AM to 9:30 PM, 30-minute intervals)
  const timeSlots = Array.from({ length: 30 }, (_, i) => {
    // Create a new date object with today's date but with specific hours and minutes
    const start = new Date();
    // Set hours to 7 AM + i*30 minutes
    start.setHours(7, i * 30, 0, 0);
    return start;
  });

  // Generate days of the week
  const days = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  // Fetch assigned tasks
  const fetchTasks = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const conditionParam = { rules: [{ operator: 'eq', field: 'task_status_id', value: '6' }] };
      const url = `${BASE_URL}/jsonapi/transform/task_list?sortBy=created_at&sortOrder=desc&page=1&pageSize=100&condition=${encodeURIComponent(JSON.stringify(conditionParam))}`;

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth?.user?.access_token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }

      const data = await response.json();
      const mappedTasks = data.data.map((item: any) => ({
        id: item.task_id,
        establishment_id: item.establishment_id,
        establishment: {
          id: item.establishment_id,
          name_en: item.establishment?.name_en || '',
          name_ar: item.establishment?.name_ar || '',
        },
      }));

      setTasks(mappedTasks);
    } catch (err) {
      setError(t('errors.fetchTasksFailed'));
      console.error('Failed to fetch tasks:', err);
    } finally {
      setIsLoading(false);
    }
  }, [auth, t]);

  // Fetch bookings
  const fetchBookings = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const url = `${BASE_URL}/sdp/rules/api/v2/task/getbookings`;
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth?.user?.access_token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }

      const data = await response.json();
      // Convert booking times to local time
      const localBookings = (data.data || []).map((booking: Booking) => {
        // Explicitly treat API times as UTC by appending 'Z'
        const utcStartTime = booking.start_time + (booking.start_time.endsWith('Z') ? '' : 'Z');
        const utcEndTime = booking.end_time + (booking.end_time.endsWith('Z') ? '' : 'Z');

        // Fetch additional details if available
        const establishment = booking.establishment || { name_en: '', name_ar: '' };
        const person = booking.person || { full_name_en: '', full_name_ar: '' };

        return {
          ...booking,
          // Store the original UTC times for API calls
          original_start_time: booking.start_time,
          original_end_time: booking.end_time,
          // Convert UTC times to local Date objects
          start_time: new Date(utcStartTime),
          end_time: new Date(utcEndTime),
          // Add establishment and person details for display
          establishment,
          person
        };
      });
      setBookings(localBookings);
    } catch (err) {
      setError(t('errors.fetchBookingsFailed'));
      console.error('Failed to fetch bookings:', err);
    } finally {
      setIsLoading(false);
    }
  }, [auth, t]);

  // Fetch establishment owners
  const fetchOwners = useCallback(async (establishmentId: string) => {
    try {
      const condition = {
        rules: [{ operator: 'eq', field: 'establishment_id', value: establishmentId }],
      };
      const url = `${BASE_URL}/sdp/api/v1/vw_EstablishmentOwners?condition=${encodeURIComponent(JSON.stringify(condition))}`;
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth?.user?.access_token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }

      const data = await response.json();
      setOwners(data.data || []);
    } catch (err) {
      console.error('Failed to fetch owners:', err);
      setToast({ message: t('errors.fetchOwnersFailed'), type: 'error' });
    }
  }, [auth, t]);

  // Initial data fetch
  useEffect(() => {
    fetchTasks();
    fetchBookings();
  }, [fetchTasks, fetchBookings]);

  // Add translation keys for new features
  useEffect(() => {
    // This is just to remind you to add these keys to the locale files
    const newKeys = [
      'schedule.bookingSummary',
      'schedule.cancel',
      'success.bookingCancelled',
      'errors.bookingCancellationFailed'
    ];
  }, []);

  // Handle week navigation
  const changeWeek = (direction: 'prev' | 'next') => {
    setCurrentWeekStart(prev => addDays(prev, direction === 'next' ? 7 : -7));
  };

  // Check if a time slot is booked and return the booking if it is
  const getBookingForSlot = (start: Date, day: Date): Booking | null => {
    const slotDateTime = new Date(day);
    slotDateTime.setHours(start.getHours(), start.getMinutes(), 0, 0);

    const booking = bookings.find(booking => {
      // Convert UTC ISO strings to local date objects
      const bookingStart = new Date(booking.start_time);
      const bookingEnd = new Date(booking.end_time);

      return (
        isSameDay(slotDateTime, bookingStart) &&
        slotDateTime >= bookingStart &&
        slotDateTime < bookingEnd
      );
    });

    return booking || null;
  };

  // Check if a time slot is booked
  const isSlotBooked = (start: Date, day: Date): boolean => {
    return getBookingForSlot(start, day) !== null;
  };

  // Handle slot selection
  const handleSlotClick = (start: Date, day: Date) => {
    const booking = getBookingForSlot(start, day);

    // If the slot is already booked, show the summary view
    if (booking) {
      setSelectedBooking(booking);
      setShowSummaryView(true);
      return;
    }

    if (tasks.length === 0) {
      setToast({ message: t('errors.noAssignedTasks'), type: 'error' });
      return;
    }

    const slotDateTime = new Date(day);
    slotDateTime.setHours(start.getHours(), start.getMinutes(), 0, 0);

    // Check if the slot and duration are available
    const endTime = addMinutes(slotDateTime, slotDuration);
    const isDurationAvailable = !bookings.some(booking => {
      // Convert UTC ISO strings to local date objects
      const bookingStart = new Date(booking.start_time);
      const bookingEnd = new Date(booking.end_time);

      return (
        isSameDay(slotDateTime, bookingStart) &&
        (
          (slotDateTime >= bookingStart && slotDateTime < bookingEnd) ||
          (endTime > bookingStart && endTime <= bookingEnd) ||
          (slotDateTime <= bookingStart && endTime >= bookingEnd)
        )
      );
    });

    if (!isDurationAvailable) {
      setToast({ message: t('errors.slotOverlap'), type: 'error' });
      return;
    }

    setSelectedSlot({
      start: slotDateTime,
      end: endTime,
      isBooked: false,
    });

    // For new bookings, we need to store local times in a format the API expects (UTC without Z)
    const startISOString = slotDateTime.toISOString();
    const endISOString = endTime.toISOString();

    // Remove the 'Z' suffix to match API format
    const startUTCForAPI = startISOString.substring(0, startISOString.length - 1);
    const endUTCForAPI = endISOString.substring(0, endISOString.length - 1);

    setFormData({
      ...formData,
      // Store UTC times without Z for API
      start_time: startUTCForAPI,
      end_time: endUTCForAPI,
      // Add local date objects for display
      start_date: slotDateTime,
      end_date: endTime
    });
    setShowBookingForm(true);
  };

  // Handle form submission
  const handleBookingSubmit = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Validate form
      if (!formData.task_id || !formData.person_id || !formData.location) {
        setToast({ message: t('errors.fillAllFields'), type: 'error' });
        setIsLoading(false);
        return;
      }

      // Check for duplicate bookings
      const hasDuplicate = bookings.some(booking =>
        booking.establishment_id === formData.establishment_id &&
        booking.task_id === formData.task_id &&
        (
          (formData.start_date < (booking.end_time instanceof Date ? booking.end_time : new Date(booking.end_time)) &&
           formData.end_date > (booking.start_time instanceof Date ? booking.start_time : new Date(booking.start_time)))
        )
      );

      if (hasDuplicate) {
        setToast({ message: t('errors.duplicateBooking'), type: 'error' });
        setIsLoading(false);
        return;
      }

      // The API expects times in UTC format without the 'Z' suffix
      // We're already storing them in this format in formData.start_time and formData.end_time
      const apiFormData = {
        ...formData,
        // Remove start_date and end_date as they're not needed for the API
        start_date: undefined,
        end_date: undefined
      };

      const response = await fetch(`${BASE_URL}/sdp/rules/api/v2/task/actions/create_booking`, {
        method: 'POST',
        body: JSON.stringify(apiFormData),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth?.user?.access_token}`,
        },
      });

      if (response.ok) {
        setToast({ message: t('success.bookingCreated'), type: 'success' });
        setShowBookingForm(false);
        setSelectedSlot(null);
        fetchBookings(); // Refresh bookings
      } else {
        throw new Error('Booking failed');
      }
    } catch (err) {
      setToast({ message: t('errors.bookingFailed'), type: 'error' });
      console.error('Failed to create booking:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle cancellation of a booking
  const handleCancelBooking = async (bookingId: string) => {
    try {
      setIsLoading(true);

      const response = await fetch(`${BASE_URL}/sdp/rules/api/v2/task/actions/cancel_booking`, {
        method: 'POST',
        body: JSON.stringify({
          booking_id: bookingId
        }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth?.user?.access_token}`,
        },
      });

      if (response.ok) {
        setToast({ message: t('success.bookingCancelled'), type: 'success' });
        setShowSummaryView(false);
        setSelectedBooking(null);
        fetchBookings(); // Refresh bookings
      } else {
        throw new Error('Booking cancellation failed');
      }
    } catch (err) {
      setToast({ message: t('errors.bookingCancellationFailed'), type: 'error' });
      console.error('Failed to cancel booking:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle task selection
  const handleTaskSelect = (taskId: string) => {
    const selectedTask = tasks.find(task => task.id === taskId);
    if (selectedTask) {
      setFormData({
        ...formData,
        task_id: taskId,
        establishment_id: selectedTask.establishment_id,
      });
      setOwners([]); // Reset owners
      setOwnerSearch('');
      fetchOwners(selectedTask.establishment_id);
    }
  };

  // Handle slot duration change
  const handleDurationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const duration = parseInt(e.target.value);
    setSlotDuration(duration);
    if (selectedSlot) {
      const newEndTime = addMinutes(selectedSlot.start, duration);
      setSelectedSlot({ ...selectedSlot, end: newEndTime });

      // Update end time for API (UTC without Z) and keep Date object for display
      const endISOString = newEndTime.toISOString();
      const endUTCForAPI = endISOString.substring(0, endISOString.length - 1);

      setFormData({
        ...formData,
        end_time: endUTCForAPI,
        end_date: newEndTime
      });
    }
  };

  // Filter tasks based on search
  const filteredTasks = tasks.filter(task =>
    task.establishment[language === 'en' ? 'name_en' : 'name_ar']
      .toLowerCase()
      .includes(taskSearch.toLowerCase())
  );

  // Filter owners based on search
  const filteredOwners = owners.filter(owner =>
    (language === 'en' ? owner.full_name_en : owner.full_name_ar)?.toLowerCase()?.includes(ownerSearch.toLowerCase())
  );

  // Summary View Modal
  const SummaryView = () => {
    if (!showSummaryView || !selectedBooking) return null;

    const bookingStart = new Date(selectedBooking.start_time);
    const bookingEnd = new Date(selectedBooking.end_time);
    const establishmentName = selectedBooking.establishment?.[language === 'en' ? 'name_en' : 'name_ar'] || '';
    const ownerName = selectedBooking.person?.[language === 'en' ? 'full_name_en' : 'full_name_ar'] || '';

    return createPortal(
      <div className="fixed inset-0 bg-[#00000080] flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">{t('schedule.bookingSummary')}</h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setShowSummaryView(false);
                setSelectedBooking(null);
              }}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-md">
              <div className="flex items-center mb-2">
                <Calendar className="w-4 h-4 text-gray-500 mr-2" />
                <span className="text-sm font-medium text-gray-700">
                  {bookingStart.toLocaleDateString()} {bookingStart.toLocaleTimeString()} - {bookingEnd.toLocaleTimeString()}
                </span>
              </div>

              <div className="flex items-start mb-2">
                <div className="w-4 h-4 text-gray-500 mr-2 mt-1 flex-shrink-0">🏢</div>
                <span className="text-sm text-gray-700">{establishmentName}</span>
              </div>

              <div className="flex items-start mb-2">
                <div className="w-4 h-4 text-gray-500 mr-2 mt-1 flex-shrink-0">👤</div>
                <span className="text-sm text-gray-700">{ownerName}</span>
              </div>

              <div className="flex items-start mb-2">
                <div className="w-4 h-4 text-gray-500 mr-2 mt-1 flex-shrink-0">📍</div>
                <span className="text-sm text-gray-700">
                  {selectedBooking.visit_type === 'on-site' ? t('schedule.onSite') : t('schedule.virtual')}
                  {selectedBooking.location ? ` - ${selectedBooking.location}` : ''}
                </span>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSummaryView(false);
                  setSelectedBooking(null);
                }}
              >
                {t('common.close')}
              </Button>
              <Button
                variant="outline"
                className="border-red-500 text-red-600 hover:bg-red-50"
                onClick={() => handleCancelBooking(selectedBooking.id)}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                ) : null}
                {t('schedule.cancel')}
              </Button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // Toast component
  const Toast = () => {
    if (!toast) return null;
    return createPortal(
      <div className="fixed bottom-4 right-4 z-[9999]">
        <div
          className={`rounded-md shadow-md p-4 flex items-center ${
            toast.type === 'success'
              ? 'bg-green-50 border-l-4 border-green-500'
              : 'bg-red-50 border-l-4 border-red-500'
          }`}
        >
          <div
            className={`mr-3 ${
              toast.type === 'success' ? 'text-green-500' : 'text-red-500'
            }`}
          >
            {toast.type === 'success' ? (
              <Check className="w-5 h-5" />
            ) : (
              <AlertTriangle className="w-5 h-5" />
            )}
          </div>
          <p
            className={`text-sm font-medium ${
              toast.type === 'success' ? 'text-green-800' : 'text-red-800'
            }`}
          >
            {toast.message}
          </p>
          <button
            onClick={() => setToast(null)}
            className="ml-4 text-gray-400 hover:text-gray-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>,
      document.body
    );
  };

  // Booking Form Modal
  const BookingForm = () => {
    if (!showBookingForm || !selectedSlot) return null;

    return (
      <div className="fixed inset-0 bg-[#00000080] flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">{t('schedule.bookAppointment')}</h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setShowBookingForm(false);
                setSelectedSlot(null);
              }}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-4">
            {/* Task Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('schedule.task')}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder={t('schedule.searchTasks')}
                  className="pl-10"
                  value={taskSearch}
                  onChange={(e) => setTaskSearch(e.target.value)}
                />
              </div>
              <select
                className="mt-2 w-full border border-gray-300 rounded-md p-2"
                value={formData.task_id}
                onChange={(e) => handleTaskSelect(e.target.value)}
              >
                <option value="">{t('schedule.selectTask')}</option>
                {filteredTasks.map(task => (
                  <option key={task.id} value={task.id}>
                    {task.establishment[language === 'en' ? 'name_en' : 'name_ar']}
                  </option>
                ))}
              </select>
            </div>

            {/* Owner Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('schedule.owner')}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder={t('schedule.searchOwners')}
                  className="pl-10"
                  value={ownerSearch}
                  onChange={(e) => setOwnerSearch(e.target.value)}
                />
              </div>
              <select
                className="mt-2 w-full border border-gray-300 rounded-md p-2"
                value={formData.person_id}
                onChange={(e) => setFormData({ ...formData, person_id: e.target.value })}
                disabled={!formData.task_id}
              >
                <option value="">{t('schedule.selectOwner')}</option>
                {filteredOwners.map(owner => (
                    <option key={owner.id} value={owner.id}>
                    {language === 'en' ? owner.full_name_en : owner.full_name_ar}
                    </option>
                ))}
              </select>
            </div>

            {/* Visit Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('schedule.visitType')}
              </label>
              <select
                className="w-full border border-gray-300 rounded-md p-2"
                value={formData.visit_type}
                onChange={(e) => setFormData({ ...formData, visit_type: e.target.value })}
              >
                <option value="on-site">{t('schedule.onSite')}</option>
                <option value="virtual">{t('schedule.virtual')}</option>
              </select>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('schedule.location')}
              </label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder={t('schedule.enterLocation')}
              />
            </div>

            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('schedule.duration')}
              </label>
              <select
                className="w-full border border-gray-300 rounded-md p-2"
                value={slotDuration}
                onChange={handleDurationChange}
              >
                {[30, 60, 90, 120, 180, 240, 300, 360, 420, 480].map(minutes => (
                  <option key={minutes} value={minutes}>
                    {minutes / 60} {t(minutes === 60 ? 'schedule.hour' : 'schedule.hours')}
                  </option>
                ))}
              </select>
            </div>

            {/* Time Display */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('schedule.time')}
              </label>
              <p className="text-sm text-gray-600">
                {/* Use toLocaleString for better localized time display */}
                {selectedSlot.start.toLocaleString()} - {selectedSlot.end.toLocaleTimeString()}
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setShowBookingForm(false);
                setSelectedSlot(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              className="bg-[#b89256] text-white hover:opacity-90"
              onClick={handleBookingSubmit}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              {t('schedule.book')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen p-4 md:p-6" dir={language === 'ar' ? 'rtl' : 'ltr'}>

      <div className="flex justify-between mb-4">
        <h1 className="text-2xl font-bold mb-4">{t('schedule.title')}</h1>
      <Button
        variant="outline"
        onClick={() => navigate('/plan')}
        className="flex items-center"
      >
        <X className="w-4 h-4 mr-2" />
        {t('common.close')}
      </Button>
    </div>
      {/* Error Display */}
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertTriangle className="w-5 h-5 mr-2" />
          <span>{error}</span>
          <Button
            variant="ghost"
            size="sm"
            className="ml-4 text-xs"
            onClick={() => {
              fetchTasks();
              fetchBookings();
            }}
          >
            {t('common.retry')}
          </Button>
        </div>
      )}

      {/* Week Navigation */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="outline" size="icon" onClick={() => changeWeek('prev')}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-lg font-medium">
          {format(currentWeekStart, 'MMMM d')} - {format(addDays(currentWeekStart, 6), 'MMMM d, yyyy')}
        </span>
        <Button variant="outline" size="icon" onClick={() => changeWeek('next')}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Weekly Grid */}
      <div className="overflow-x-auto">
        <div className="grid grid-cols-[100px_repeat(7,1fr)] gap-1 min-w-[800px]">
          {/* Time Column */}
          <div className="sticky left-0 bg-white z-10">
            <div className="h-12"></div>
            {timeSlots.map(slot => (
              <div key={slot.toISOString()} className="h-12 flex items-center justify-end pr-2 text-sm text-gray-600">
                {format(slot, 'h:mm a')}
              </div>
            ))}
          </div>

          {/* Day Columns */}
          {days.map(day => (
            <div key={day.toISOString()}>
              <div className="h-12 flex items-center justify-center text-sm font-medium text-gray-700 border-b">
                {format(day, 'EEE, MMM d')}
              </div>
              {timeSlots.map(slot => {
                const booking = getBookingForSlot(slot, day);
                const isBooked = booking !== null;

                return (
                  <div
                    key={`${day.toISOString()}-${slot.toISOString()}`}
                    className={`h-12 border border-gray-200 ${
                      isBooked
                        ? 'bg-[#b89256]/20 cursor-pointer'
                        : 'bg-white hover:bg-[#b89256]/10 cursor-pointer'
                    }`}
                    onClick={() => handleSlotClick(slot, day)}
                  >
                    {isBooked && (
                      <div className="h-full flex flex-col items-center justify-center p-1">
                        <Clock className="w-3 h-3 text-[#b89256]" />
                        <div className="text-[8px] text-gray-600 truncate w-full text-center">
                          {booking.establishment?.[language === 'en' ? 'name_en' : 'name_ar']?.substring(0, 10) || ''}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-gray-100 bg-opacity-75 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#b89256] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600">{t('common.loading')}</p>
          </div>
        </div>
      )}

      {/* Booking Form Modal */}
      <BookingForm />

      {/* Summary View Modal */}
      <SummaryView />

      {/* Toast Notification */}
      <Toast />
    </div>
  );
}
